import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faCoins,
  faLandmark,
  faMoneyBillWave,
  faPenToSquare,
  faPlus,
  faReceipt,
  faScaleBalanced,
  faShieldHalved,
  faTrashCan,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import './debtsLoans.css';
import { logAdminActivity } from '../../utils/activityLogger';
import {
  formatKes,
  formatShortDate,
  getDateTimeInputValue,
  getMonthKey,
  toDateValue
} from '../../utils/financeWorkflow';

const LIABILITY_TYPES = [
  'Bank Loan',
  'Supplier / Trade Payable',
  'Personal / Individual Debt',
  'Legal Claim / Lawsuit',
  'Court Order',
  'Government Fine / Penalty',
  'Asset Finance / Lease',
  'Tax / Statutory Obligation',
  'Staff Arrears',
  'Other'
];

const LIABILITY_STATUS_OPTIONS = [
  'Active',
  'Under Review',
  'Disputed',
  'Court Ordered',
  'Restructured',
  'Settled'
];

const REPAYMENT_METHOD_OPTIONS = [
  'Bank Transfer',
  'MPESA',
  'Paybill',
  'Till Number',
  'Cash',
  'Cheque',
  'Other'
];

const PAYMENT_METHOD_OPTIONS = [
  'Bank Transfer',
  'MPESA',
  'Cash',
  'Cheque',
  'Card',
  'Paybill',
  'Till Number',
  'Other'
];

const ACTIVE_PAYMENT_STATUSES = ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'];
const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';
const LOTUS_RECONCILE_INTERVAL_MS = 15 * 60 * 1000;
const LIABILITY_PAYMENT_METHOD_TO_LOTUS = {
  'Bank Transfer': 'pesalink',
  MPESA: 'mpesa',
  Paybill: 'paybill',
  'Till Number': 'till'
};

const normalizeLotusTransactionId = (value) => String(value || '')
  .trim()
  .replace(/^TR[-_ ]*/i, '');

const generateLotusPaymentTag = () => {
  const uuid = typeof window !== 'undefined'
    && window.crypto
    && typeof window.crypto.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return `liability-group-${uuid}`;
};

const findNestedValue = (source, keys) => {
  const queue = [source];
  const seen = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || seen.has(current)) {
      continue;
    }

    seen.add(current);

    for (const key of keys) {
      const value = current[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return '';
};

const extractLotusPaymentDetails = (payload) => {
  const source = payload?.data?.transaction
    || payload?.data
    || payload?.transaction
    || payload?.payment
    || payload?.result
    || payload
    || {};

  return {
    lotusTransactionId: normalizeLotusTransactionId(findNestedValue(source, [
      'transaction_id',
      'transactionId',
      'payment_id',
      'paymentId',
      'id'
    ])),
    lotusTransactionStatus: String(findNestedValue(source, [
      'status',
      'payment_status',
      'paymentStatus',
      'state',
      'result',
      'transaction_status',
      'transactionStatus'
    ])).trim(),
    lotusTransactionMessage: String(findNestedValue(payload, [
      'message',
      'detail',
      'description',
      'response_message',
      'responseMessage'
    ])).trim()
  };
};

const mapLotusBankOptions = (payload) => {
  const rawBanks = payload?.data?.banks || payload?.data?.bank_list || payload?.banks || [];
  if (!Array.isArray(rawBanks)) return [];

  return rawBanks
    .map((bank) => {
      const bankId = String(bank?.id || bank?.bank_id || bank?.bankId || bank?.code || bank?.bank_code || '').trim();
      const bankName = String(
        bank?.name || bank?.bank_name || bank?.bankName || bank?.display_name || bank?.label || bankId || 'Bank'
      ).trim();

      return {
        id: bankId || bankName,
        name: bankName,
        code: String(bank?.code || bank?.bank_code || bank?.bankCode || '').trim()
      };
    })
    .filter((bank) => bank.id || bank.name);
};

const buildEmptyLiabilityForm = (branchId = '') => ({
  selectedCampus: branchId,
  liabilityType: LIABILITY_TYPES[0],
  counterpartyName: '',
  liabilityTitle: '',
  liabilityDescription: '',
  principalAmount: '',
  startDate: getDateTimeInputValue(),
  dueDate: '',
  liabilityStatus: LIABILITY_STATUS_OPTIONS[0],
  referenceNumber: '',
  repaymentMethod: REPAYMENT_METHOD_OPTIONS[0],
  repaymentBankId: '',
  repaymentBankName: '',
  repaymentBankCode: '',
  repaymentBankAccountNumber: '',
  repaymentMpesaNumber: '',
  repaymentPaybillNumber: '',
  repaymentPaybillAccountNumber: '',
  repaymentTillNumber: '',
  repaymentReference: '',
  notes: ''
});

const buildEmptyPaymentForm = (paymentMethod = PAYMENT_METHOD_OPTIONS[0]) => ({
  amount: '',
  paymentDate: getDateTimeInputValue(),
  paymentMethod,
  reference: '',
  notes: ''
});

const normalizeText = (value) => String(value || '').trim();

const normalizeStatus = (value) => normalizeText(value).toLowerCase();

const normalizeType = (value) => normalizeText(value) || LIABILITY_TYPES[0];

const normalizeAmount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const isSuccessfulLotusPayment = (value) => ACTIVE_PAYMENT_STATUSES.includes(normalizeStatus(value));

const resolveLiabilityPaymentStatus = (payment = {}) => {
  const candidates = [
    payment.paymentStatus,
    payment.status,
    payment.lotusTransactionStatus
  ]
    .map((value) => normalizeStatus(value))
    .filter(Boolean);

  if (candidates.some((value) => isSuccessfulLotusPayment(value))) {
    return 'Paid';
  }

  if (candidates.some((value) => ['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(value))) {
    return 'Failed';
  }

  if (candidates.some((value) => ['pending', 'processing', 'submitted'].includes(value)) || payment.lotusTransactionId) {
    return 'Pending';
  }

  return normalizeText(payment.paymentStatus || payment.status || '');
};

const isSuccessfulPayment = (payment) => resolveLiabilityPaymentStatus(payment) === 'Paid';

const getCategoryTone = (type) => {
  const normalized = normalizeType(type).toLowerCase();

  if (normalized.includes('bank loan') || normalized.includes('asset finance') || normalized.includes('lease')) {
    return 'info';
  }

  if (normalized.includes('court') || normalized.includes('lawsuit') || normalized.includes('legal') || normalized.includes('fine')) {
    return 'danger';
  }

  if (normalized.includes('tax') || normalized.includes('statutory')) {
    return 'warning';
  }

  if (normalized.includes('supplier') || normalized.includes('trade') || normalized.includes('staff arrears')) {
    return 'muted';
  }

  return 'primary';
};

const getLiabilityTone = (row) => {
  if (row.remainingAmount <= 0) {
    return 'success';
  }

  if (row.overdue) {
    return 'danger';
  }

  if (row.dueSoon) {
    return 'warning';
  }

  if (['Disputed', 'Court Ordered'].includes(row.liabilityStatus)) {
    return 'danger';
  }

  if (row.paymentCount > 0) {
    return 'info';
  }

  return 'primary';
};

const getToneLabel = (tone) => {
  switch (tone) {
    case 'success':
      return 'Settled';
    case 'danger':
      return 'Urgent';
    case 'warning':
      return 'Due soon';
    case 'info':
      return 'In progress';
    default:
      return 'Open';
  }
};

const getCategoryIcon = (type) => {
  const normalized = normalizeType(type).toLowerCase();

  if (normalized.includes('bank loan') || normalized.includes('asset finance') || normalized.includes('lease')) {
    return faLandmark;
  }

  if (normalized.includes('court') || normalized.includes('lawsuit') || normalized.includes('legal')) {
    return faScaleBalanced;
  }

  if (normalized.includes('fine') || normalized.includes('penalty') || normalized.includes('tax')) {
    return faCircleExclamation;
  }

  if (normalized.includes('supplier') || normalized.includes('trade')) {
    return faWallet;
  }

  if (normalized.includes('staff')) {
    return faShieldHalved;
  }

  return faCoins;
};

const getRepaymentSummary = (liability = {}) => {
  const method = normalizeText(liability.repaymentMethod || liability.paymentMethod || 'Not set') || 'Not set';

  if (method === 'Bank Transfer') {
    return {
      method,
      detail: [
        liability.repaymentBankName || 'Bank not set',
        liability.repaymentBankAccountNumber || 'Account not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(
        normalizeText(liability.repaymentBankName || '').length
        && normalizeText(liability.repaymentBankAccountNumber || '').length
      )
    };
  }

  if (method === 'MPESA') {
    return {
      method,
      detail: liability.repaymentMpesaNumber || 'Phone not set',
      ready: Boolean(normalizeText(liability.repaymentMpesaNumber || '').length)
    };
  }

  if (method === 'Paybill') {
    return {
      method,
      detail: [
        liability.repaymentPaybillNumber || 'Paybill not set',
        liability.repaymentPaybillAccountNumber || 'Account not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(
        normalizeText(liability.repaymentPaybillNumber || '').length
        && normalizeText(liability.repaymentPaybillAccountNumber || '').length
      )
    };
  }

  if (method === 'Till Number') {
    return {
      method,
      detail: liability.repaymentTillNumber || 'Till number not set',
      ready: Boolean(normalizeText(liability.repaymentTillNumber || '').length)
    };
  }

  if (method === 'Cash') {
    return {
      method,
      detail: liability.repaymentReference || 'Cash settlement',
      ready: true
    };
  }

  if (method === 'Cheque') {
    return {
      method,
      detail: liability.repaymentReference || 'Cheque details not set',
      ready: true
    };
  }

  return {
    method,
    detail: liability.repaymentReference || 'Repayment method set',
    ready: method !== 'Not set'
  };
};

const getLiabilityPaymentDestinationDetails = (liability = {}, paymentMethod = '') => {
  const method = normalizeText(paymentMethod || liability.repaymentMethod || liability.paymentMethod);

  if (method === 'Bank Transfer') {
    return {
      method,
      recipientName: liability.counterpartyName || liability.liabilityTitle || 'Liability payee',
      bankId: normalizeText(liability.repaymentBankId || ''),
      bankName: normalizeText(liability.repaymentBankName || ''),
      bankCode: normalizeText(liability.repaymentBankCode || ''),
      bankAccountNumber: normalizeText(liability.repaymentBankAccountNumber || '')
    };
  }

  if (method === 'MPESA') {
    return {
      method,
      recipientName: liability.counterpartyName || liability.liabilityTitle || 'Liability payee',
      phoneNumber: normalizeText(liability.repaymentMpesaNumber || '')
    };
  }

  if (method === 'Paybill') {
    return {
      method,
      recipientName: liability.counterpartyName || liability.liabilityTitle || 'Liability payee',
      paybillNumber: normalizeText(liability.repaymentPaybillNumber || ''),
      paybillAccountNumber: normalizeText(liability.repaymentPaybillAccountNumber || '')
    };
  }

  if (method === 'Till Number') {
    return {
      method,
      recipientName: liability.counterpartyName || liability.liabilityTitle || 'Liability payee',
      tillNumber: normalizeText(liability.repaymentTillNumber || '')
    };
  }

  return null;
};

function DebtsComp() {
  const auth = getAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingLiability, setSavingLiability] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [deletingLiability, setDeletingLiability] = useState(false);
  const [branchesList, setBranchesList] = useState([]);
  const [branchMapping, setBranchMapping] = useState({});
  const [bankOptions, setBankOptions] = useState([]);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankLoadError, setBankLoadError] = useState('');
  const [liabilities, setLiabilities] = useState([]);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [branchFilter, setBranchFilter] = useState('All');
  const [formMode, setFormMode] = useState('add');
  const [formShow, setFormShow] = useState(false);
  const [detailsShow, setDetailsShow] = useState(false);
  const [paymentShow, setPaymentShow] = useState(false);
  const [selectedLiability, setSelectedLiability] = useState(null);
  const [formState, setFormState] = useState(buildEmptyLiabilityForm());
  const [paymentForm, setPaymentForm] = useState(buildEmptyPaymentForm());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, [auth]);

  const loadPage = useCallback(async () => {
    const [branchSnapshot, liabilitySnapshot, paymentSnapshot] = await Promise.all([
      getDocs(collection(db, 'Branches')),
      getDocs(collection(db, 'Loans')),
      getDocs(collection(db, 'LoanPayments'))
    ]);

    const nextBranches = branchSnapshot.docs.map((branchDoc) => ({
      id: branchDoc.id,
      ...branchDoc.data()
    }));

    const nextBranchMapping = nextBranches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});

    setBranchesList(nextBranches);
    setBranchMapping(nextBranchMapping);

    const nextLiabilities = liabilitySnapshot.docs.map((itemDoc) => ({
      id: itemDoc.id,
      ...itemDoc.data()
    }));

    const nextPayments = paymentSnapshot.docs.map((itemDoc) => ({
      id: itemDoc.id,
      ...itemDoc.data()
    }));

    setLiabilities(nextLiabilities);
    setPayments(nextPayments.map((payment) => {
      const paymentStatus = resolveLiabilityPaymentStatus(payment) || payment.paymentStatus || payment.status || '';
      return {
        ...payment,
        paymentStatus,
        status: paymentStatus,
        lotusTransactionId: payment.lotusTransactionId || '',
        lotusTransactionStatus: payment.lotusTransactionStatus || ''
      };
    }));
  }, []);

  const loadLotusBanks = useCallback(async ({ force = false } = {}) => {
    if ((banksLoaded && !force) || loadingBanks) {
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setBankLoadError('Sign in again to load bank options.');
      return;
    }

    setLoadingBanks(true);
    setBankLoadError('');

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${LOTUS_API_BASE_URL}/banks`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to load banks.');
      }

      setBankOptions(mapLotusBankOptions(payload));
      setBanksLoaded(true);
    } catch (error) {
      console.error('Failed to load Lotus banks:', error);
      setBankLoadError('Banks could not be loaded right now.');
    } finally {
      setLoadingBanks(false);
    }
  }, [auth, banksLoaded, loadingBanks]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load liabilities page:', error);
        swal('Error', 'Liabilities could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadPage]);

  useEffect(() => {
    if (branchesList.length === 0) {
      return;
    }

    setFormState((currentForm) => (
      currentForm.selectedCampus
        ? currentForm
        : {
            ...currentForm,
            selectedCampus: branchesList[0]?.id || 'General'
          }
    ));
  }, [branchesList]);

  useEffect(() => {
    if (formShow && formState.repaymentMethod === 'Bank Transfer' && !banksLoaded) {
      loadLotusBanks();
    }
  }, [banksLoaded, formShow, formState.repaymentMethod, loadLotusBanks]);

  const liabilityRows = useMemo(() => {
    const paymentBuckets = payments.reduce((accumulator, payment) => {
      const liabilityId = normalizeText(payment.liabilityId || payment.loanId || payment.debtId);
      if (!liabilityId) {
        return accumulator;
      }

      if (!accumulator[liabilityId]) {
        accumulator[liabilityId] = [];
      }

      accumulator[liabilityId].push(payment);
      return accumulator;
    }, {});

    const dueSoonThreshold = new Date();
    dueSoonThreshold.setDate(dueSoonThreshold.getDate() + 30);
    const now = new Date();

    return liabilities
      .map((liability) => {
        const liabilityPayments = [...(paymentBuckets[liability.id] || [])].sort((first, second) => {
          const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
          const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
          return secondTime - firstTime;
        });
        const successfulPayments = liabilityPayments.filter((payment) => isSuccessfulPayment(payment));

        const principalAmount = normalizeAmount(
          liability.principalAmount
          || liability.loanAmount
          || liability.amount
        );
        const totalPaidAmount = successfulPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(principalAmount - totalPaidAmount, 0);
        const startDate = toDateValue(liability.startDate || liability.dateBorrowed || liability.timeStamp);
        const dueDate = toDateValue(liability.dueDate);
        const branchId = liability.selectedCampus || liability.branchId || 'General';
        const liabilityStatus = normalizeText(liability.liabilityStatus || liability.status || 'Active');
        const category = normalizeType(liability.liabilityType || liability.category || 'Other');
        const overdue = remainingAmount > 0 && Boolean(dueDate) && dueDate < now;
        const dueSoon = remainingAmount > 0 && Boolean(dueDate) && dueDate <= dueSoonThreshold && dueDate >= now;
        const paymentStatus = remainingAmount <= 0
          ? 'Settled'
          : totalPaidAmount > 0
            ? 'Partially paid'
            : 'Outstanding';

        return {
          id: liability.id,
          ...liability,
          selectedCampus: branchId,
          branchName: branchId === 'General' ? 'General' : branchMapping[branchId] || 'Campus not set',
          liabilityType: category,
          liabilityStatus,
          liabilityTitle: normalizeText(liability.liabilityTitle || liability.loanDesc || 'Liability'),
          liabilityDescription: normalizeText(liability.liabilityDescription || liability.loanDesc || ''),
          counterpartyName: normalizeText(liability.counterpartyName || liability.personWeOwe || liability.lenderName || 'Counterparty'),
          repaymentMethod: normalizeText(liability.repaymentMethod || liability.paymentMethod || 'Not set') || 'Not set',
          repaymentBankId: normalizeText(liability.repaymentBankId || ''),
          repaymentBankName: normalizeText(liability.repaymentBankName || ''),
          repaymentBankCode: normalizeText(liability.repaymentBankCode || ''),
          repaymentBankAccountNumber: normalizeText(liability.repaymentBankAccountNumber || ''),
          repaymentMpesaNumber: normalizeText(liability.repaymentMpesaNumber || ''),
          repaymentPaybillNumber: normalizeText(liability.repaymentPaybillNumber || ''),
          repaymentPaybillAccountNumber: normalizeText(liability.repaymentPaybillAccountNumber || ''),
          repaymentTillNumber: normalizeText(liability.repaymentTillNumber || ''),
          repaymentReference: normalizeText(liability.repaymentReference || ''),
          principalAmount,
          totalPaidAmount,
          remainingAmount,
          paymentCount: liabilityPayments.length,
          verifiedPaymentCount: successfulPayments.length,
          lastPaymentAt: liabilityPayments[0] ? (liabilityPayments[0].paymentDate || liabilityPayments[0].createdAt || null) : null,
          startDate,
          dueDate,
          overdue,
          dueSoon,
          paymentStatus,
          paymentTone: getLiabilityTone({
            remainingAmount,
            overdue,
            dueSoon,
            liabilityStatus,
            paymentCount: successfulPayments.length
          }),
          categoryTone: getCategoryTone(category),
          paymentHistory: liabilityPayments
        };
      })
      .sort((first, second) => {
        if (second.remainingAmount !== first.remainingAmount) {
          return second.remainingAmount - first.remainingAmount;
        }

        const firstTime = toDateValue(first.dueDate || first.startDate || first.createdAt)?.getTime() || 0;
        const secondTime = toDateValue(second.dueDate || second.startDate || second.createdAt)?.getTime() || 0;
        return secondTime - firstTime;
      });
  }, [branchMapping, liabilities, payments]);

  const summary = useMemo(() => {
    const now = new Date();
    const monthKey = getMonthKey(now);

    const totals = liabilityRows.reduce((accumulator, liability) => {
      accumulator.outstanding += liability.remainingAmount;
      accumulator.openCount += liability.remainingAmount > 0 ? 1 : 0;
      accumulator.settledCount += liability.remainingAmount <= 0 ? 1 : 0;
      accumulator.dueSoonCount += liability.dueSoon || liability.overdue ? 1 : 0;
      accumulator.legalRiskCount += [
        'legal claim / lawsuit',
        'court order',
        'government fine / penalty',
        'tax / statutory obligation'
      ].includes(normalizeType(liability.liabilityType).toLowerCase())
        || liability.overdue
        ? 1
        : 0;
      return accumulator;
    }, {
      outstanding: 0,
      openCount: 0,
      settledCount: 0,
      dueSoonCount: 0,
      legalRiskCount: 0
    });

    totals.paidThisMonth = payments
      .filter((payment) => isSuccessfulPayment(payment))
      .filter((payment) => getMonthKey(payment.paymentDate || payment.createdAt) === monthKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    return totals;
  }, [liabilityRows, payments]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return liabilityRows.filter((liability) => {
      const matchesBranch =
        branchFilter === 'All' ||
        (branchFilter === 'General' && liability.selectedCampus === 'General') ||
        liability.selectedCampus === branchFilter;
      const matchesType = typeFilter === 'All' || normalizeType(liability.liabilityType) === typeFilter;
      const matchesStatus = statusFilter === 'All' || normalizeText(liability.liabilityStatus) === statusFilter;

      if (!matchesBranch || !matchesType || !matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        liability.liabilityTitle,
        liability.liabilityDescription,
        liability.counterpartyName,
        liability.branchName,
        liability.liabilityType,
        liability.referenceNumber
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [branchFilter, liabilityRows, searchTerm, statusFilter, typeFilter]);

  const clearForm = () => {
    setFormState(buildEmptyLiabilityForm(branchesList[0]?.id || 'General'));
    setSelectedLiability(null);
  };

  const openCreateModal = () => {
    setFormMode('add');
    setFormState(buildEmptyLiabilityForm(branchesList[0]?.id || 'General'));
    setFormShow(true);
  };

  const openEditModal = (liability) => {
    setFormMode('edit');
    setSelectedLiability(liability);
    setFormState({
      selectedCampus: liability.selectedCampus || 'General',
      liabilityType: liability.liabilityType || LIABILITY_TYPES[0],
      counterpartyName: liability.counterpartyName || liability.personWeOwe || '',
      liabilityTitle: liability.liabilityTitle || liability.loanDesc || '',
      liabilityDescription: liability.liabilityDescription || liability.loanDesc || '',
      principalAmount: String(liability.principalAmount || liability.loanAmount || liability.amount || ''),
      startDate: liability.startDate
        ? getDateTimeInputValue(toDateValue(liability.startDate))
        : liability.dateBorrowed
          ? getDateTimeInputValue(toDateValue(liability.dateBorrowed))
          : getDateTimeInputValue(),
      dueDate: liability.dueDate ? getDateTimeInputValue(toDateValue(liability.dueDate)) : '',
      liabilityStatus: liability.liabilityStatus || liability.status || LIABILITY_STATUS_OPTIONS[0],
      referenceNumber: liability.referenceNumber || liability.docRef || '',
      repaymentMethod: liability.repaymentMethod || liability.paymentMethod || REPAYMENT_METHOD_OPTIONS[0],
      repaymentBankId: liability.repaymentBankId || '',
      repaymentBankName: liability.repaymentBankName || '',
      repaymentBankCode: liability.repaymentBankCode || '',
      repaymentBankAccountNumber: liability.repaymentBankAccountNumber || '',
      repaymentMpesaNumber: liability.repaymentMpesaNumber || '',
      repaymentPaybillNumber: liability.repaymentPaybillNumber || '',
      repaymentPaybillAccountNumber: liability.repaymentPaybillAccountNumber || '',
      repaymentTillNumber: liability.repaymentTillNumber || '',
      repaymentReference: liability.repaymentReference || '',
      notes: liability.notes || ''
    });
    setFormShow(true);
  };

  const openDetailsModal = (liability) => {
    setSelectedLiability(liability);
    setDetailsShow(true);
  };

  const openPaymentModal = (liability) => {
    setSelectedLiability(liability);
    const preferredMethod = normalizeText(liability?.repaymentMethod || liability?.paymentMethod);
    setPaymentForm(buildEmptyPaymentForm(
      LIABILITY_PAYMENT_METHOD_TO_LOTUS[preferredMethod]
        ? preferredMethod
        : PAYMENT_METHOD_OPTIONS[0]
    ));
    setPaymentShow(true);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((currentForm) => {
      if (name === 'repaymentMethod') {
        return {
          ...currentForm,
          repaymentMethod: value,
          repaymentBankId: value === 'Bank Transfer' ? currentForm.repaymentBankId : '',
          repaymentBankName: value === 'Bank Transfer' ? currentForm.repaymentBankName : '',
          repaymentBankCode: value === 'Bank Transfer' ? currentForm.repaymentBankCode : '',
          repaymentBankAccountNumber: value === 'Bank Transfer' ? currentForm.repaymentBankAccountNumber : '',
          repaymentMpesaNumber: value === 'MPESA' ? currentForm.repaymentMpesaNumber : '',
          repaymentPaybillNumber: value === 'Paybill' ? currentForm.repaymentPaybillNumber : '',
          repaymentPaybillAccountNumber: value === 'Paybill' ? currentForm.repaymentPaybillAccountNumber : '',
          repaymentTillNumber: value === 'Till Number' ? currentForm.repaymentTillNumber : '',
          repaymentReference: currentForm.repaymentReference
        };
      }

      if (name === 'repaymentBankId') {
        const selectedBank = bankOptions.find((bank) => bank.id === value);
        return {
          ...currentForm,
          repaymentBankId: value,
          repaymentBankName: selectedBank?.name || '',
          repaymentBankCode: selectedBank?.code || ''
        };
      }

      return {
        ...currentForm,
        [name]: value
      };
    });
  };

  const handlePaymentFormChange = (event) => {
    const { name, value } = event.target;
    setPaymentForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const submitLiability = async () => {
    const principalValue = normalizeAmount(formState.principalAmount);
    const startDateValue = formState.startDate ? new Date(formState.startDate) : null;
    const dueDateValue = formState.dueDate ? new Date(formState.dueDate) : null;

    if (!formState.selectedCampus || !normalizeText(formState.counterpartyName) || !normalizeText(formState.liabilityTitle)) {
      swal('Missing details', 'Please choose a campus, counterparty, and title.', 'warning');
      return;
    }

    if (!Number.isFinite(principalValue) || principalValue <= 0) {
      swal('Invalid amount', 'Principal amount should be greater than zero.', 'warning');
      return;
    }

    if (startDateValue && Number.isNaN(startDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid start date.', 'warning');
      return;
    }

    if (dueDateValue && Number.isNaN(dueDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid due date.', 'warning');
      return;
    }

    setSavingLiability(true);

    try {
      const liabilityRef = formMode === 'edit' && selectedLiability
        ? doc(db, 'Loans', selectedLiability.id)
        : doc(collection(db, 'Loans'));

      const currentRow = liabilityRows.find((item) => item.id === (selectedLiability?.id || liabilityRef.id));
      const payload = {
        selectedCampus: formState.selectedCampus,
        liabilityType: formState.liabilityType,
        liabilityTitle: normalizeText(formState.liabilityTitle),
        liabilityDescription: normalizeText(formState.liabilityDescription),
        counterpartyName: normalizeText(formState.counterpartyName),
        repaymentMethod: normalizeText(formState.repaymentMethod || REPAYMENT_METHOD_OPTIONS[0]),
        repaymentBankId: normalizeText(formState.repaymentBankId),
        repaymentBankName: normalizeText(formState.repaymentBankName),
        repaymentBankCode: normalizeText(formState.repaymentBankCode),
        repaymentBankAccountNumber: normalizeText(formState.repaymentBankAccountNumber),
        repaymentMpesaNumber: normalizeText(formState.repaymentMpesaNumber),
        repaymentPaybillNumber: normalizeText(formState.repaymentPaybillNumber),
        repaymentPaybillAccountNumber: normalizeText(formState.repaymentPaybillAccountNumber),
        repaymentTillNumber: normalizeText(formState.repaymentTillNumber),
        repaymentReference: normalizeText(formState.repaymentReference),
        principalAmount: principalValue,
        remainingAmount: currentRow ? currentRow.remainingAmount : principalValue,
        totalPaidAmount: currentRow ? currentRow.totalPaidAmount : 0,
        paymentCount: currentRow ? currentRow.paymentCount : 0,
        startDate: startDateValue ? Timestamp.fromDate(startDateValue) : null,
        dueDate: dueDateValue ? Timestamp.fromDate(dueDateValue) : null,
        liabilityStatus: formState.liabilityStatus,
        referenceNumber: normalizeText(formState.referenceNumber),
        notes: normalizeText(formState.notes),
        docID: liabilityRef.id,
        updatedAt: serverTimestamp(),
        updatedById: currentUser?.uid || '',
        updatedByName: currentUser?.displayName || currentUser?.email || 'Finance'
      };

      if (formMode === 'add') {
        payload.createdAt = serverTimestamp();
        payload.createdById = currentUser?.uid || '';
        payload.createdByName = currentUser?.displayName || currentUser?.email || 'Finance';
      }

      await setDoc(liabilityRef, payload, { merge: true });

      logAdminActivity({
        action: formMode === 'edit' ? 'Updated liability record' : 'Created liability record',
        details: `${formMode === 'edit' ? 'Updated' : 'Added'} ${formState.liabilityTitle} for ${formState.counterpartyName}.`,
        meta: {
          targetCollection: 'Loans',
          targetId: liabilityRef.id,
          liabilityType: formState.liabilityType
        }
      });

      swal('Success!', formMode === 'edit' ? 'Liability updated successfully.' : 'Liability added successfully.', 'success');
      setFormShow(false);
      clearForm();
      await loadPage();
    } catch (error) {
      console.error('Failed to save liability:', error);
      swal('Error', 'The liability could not be saved right now.', 'error');
    } finally {
      setSavingLiability(false);
    }
  };

  const submitPayment = async () => {
    if (!selectedLiability) {
      return;
    }

    const paymentAmount = normalizeAmount(paymentForm.amount);
    const paymentDateValue = paymentForm.paymentDate ? new Date(paymentForm.paymentDate) : null;

    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (paymentAmount > selectedLiability.remainingAmount) {
      swal(
        'Amount too high',
        `You can only pay up to ${formatKes(selectedLiability.remainingAmount)} for this liability.`,
        'warning'
      );
      return;
    }

    if (!paymentDateValue || Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    const paymentMethod = normalizeText(paymentForm.paymentMethod || selectedLiability.repaymentMethod || PAYMENT_METHOD_OPTIONS[0]);
    const lotusPaymentMethod = LIABILITY_PAYMENT_METHOD_TO_LOTUS[paymentMethod];
    if (!lotusPaymentMethod) {
      swal(
        'Unsupported method',
        'Lotus only processes bank transfers, MPESA, paybill, and till payments for this flow.',
        'warning'
      );
      return;
    }

    const destinationDetails = getLiabilityPaymentDestinationDetails(selectedLiability, paymentMethod);
    if (!destinationDetails) {
      swal('Missing payout details', 'We could not prepare the payout destination for this liability.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'pesalink' && (!destinationDetails.bankId || !destinationDetails.bankAccountNumber)) {
      swal('Missing bank details', 'Add the bank name and bank account number before submitting this payment.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'mpesa' && !destinationDetails.phoneNumber) {
      swal('Missing phone number', 'Add the MPESA phone number before submitting this payment.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'paybill' && (!destinationDetails.paybillNumber || !destinationDetails.paybillAccountNumber)) {
      swal('Missing paybill details', 'Add the paybill number and account number before submitting this payment.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'till' && !destinationDetails.tillNumber) {
      swal('Missing till number', 'Add the till number before submitting this payment.', 'warning');
      return;
    }

    setSavingPayment(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        swal('Session expired', 'Please sign in again before submitting a liability payment.', 'warning');
        return;
      }

      const idToken = await currentUser.getIdToken();
      const paymentTag = generateLotusPaymentTag();
      const paymentTimestamp = Timestamp.fromDate(paymentDateValue);
      const paymentRef = doc(collection(db, 'LoanPayments'));
      const lotusPayload = {
        amount: paymentAmount,
        payment_method: lotusPaymentMethod,
        recipient_name: destinationDetails.recipientName || selectedLiability.counterpartyName || 'Liability payee',
        payment_purpose: `Liability payment for ${selectedLiability.liabilityTitle || 'liability'} - ${selectedLiability.counterpartyName || 'counterparty'}`,
        payment_tag: paymentTag,
        payment_category: 'liability_payment'
      };

      if (lotusPaymentMethod === 'pesalink') {
        lotusPayload.bank_id = destinationDetails.bankId;
        lotusPayload.bank_account_number = destinationDetails.bankAccountNumber;
      }

      if (lotusPaymentMethod === 'mpesa') {
        lotusPayload.phone_number = destinationDetails.phoneNumber;
      }

      if (lotusPaymentMethod === 'paybill') {
        lotusPayload.paybill_number = destinationDetails.paybillNumber;
        lotusPayload.paybill_account_number = destinationDetails.paybillAccountNumber;
      }

      if (lotusPaymentMethod === 'till') {
        lotusPayload.till_number = destinationDetails.tillNumber;
      }

      const lotusResponse = await fetch(`${LOTUS_API_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lotusPayload)
      });

      const lotusResponseBody = await lotusResponse.json().catch(() => null);
      if (!lotusResponse.ok) {
        const lotusErrorMessage = lotusResponseBody?.error?.message
          || lotusResponseBody?.error?.detail
          || lotusResponseBody?.error
          || lotusResponseBody?.message
          || 'Lotus rejected the payment request.';
        throw new Error(typeof lotusErrorMessage === 'string' ? lotusErrorMessage : 'Lotus rejected the payment request.');
      }

      const lotusPaymentDetails = extractLotusPaymentDetails(lotusResponseBody);
      const lotusPaymentData = lotusResponseBody?.data?.transaction
        || lotusResponseBody?.data
        || lotusResponseBody
        || {};
      const lotusTransactionId = lotusPaymentDetails.lotusTransactionId;
      const lotusTransactionStatus = lotusPaymentDetails.lotusTransactionStatus || 'pending';
      const lotusTransactionMessage = lotusPaymentDetails.lotusTransactionMessage
        || lotusResponseBody?.message
        || 'Payment submitted to Lotus.';
      const paymentPayload = {
        liabilityId: selectedLiability.id,
        liabilityTitle: selectedLiability.liabilityTitle,
        liabilityType: selectedLiability.liabilityType,
        counterpartyName: selectedLiability.counterpartyName,
        branchId: selectedLiability.selectedCampus,
        branchName: selectedLiability.branchName,
        amount: paymentAmount,
        paymentDate: paymentTimestamp,
        paymentMethod,
        reference: normalizeText(paymentForm.reference),
        notes: normalizeText(paymentForm.notes),
        paymentStatus: 'Pending',
        status: 'Pending',
        createdAt: Timestamp.now(),
        createdById: currentUser?.uid || '',
        createdByName: currentUser?.displayName || currentUser?.email || 'Finance',
        docID: paymentRef.id,
        transactionGroupId: paymentTag,
        lotusPaymentTag: paymentTag,
        lotusPaymentMethod,
        lotusTransactionId,
        lotusTransactionStatus,
        lotusTransactionMessage,
        lotusPaymentResponse: lotusPaymentData,
        paymentDestinationSummary: lotusPaymentMethod === 'pesalink'
          ? {
              method: paymentMethod,
              bankId: destinationDetails.bankId,
              bankName: destinationDetails.bankName,
              bankCode: destinationDetails.bankCode,
              bankAccountLast4: destinationDetails.bankAccountNumber.slice(-4)
            }
          : lotusPaymentMethod === 'mpesa'
            ? {
                method: paymentMethod,
                phoneLast4: destinationDetails.phoneNumber.slice(-4)
              }
            : lotusPaymentMethod === 'paybill'
              ? {
                  method: paymentMethod,
                  paybillNumber: destinationDetails.paybillNumber,
                  accountLast4: destinationDetails.paybillAccountNumber.slice(-4)
                }
              : {
                  method: paymentMethod,
                  tillNumber: destinationDetails.tillNumber
                },
        sourcePage: 'debts-loans'
      };

      await setDoc(paymentRef, paymentPayload);
      const createdPayment = { id: paymentRef.id, ...paymentPayload };

      logAdminActivity({
        action: 'Recorded liability payment',
        details: `Submitted ${formatKes(paymentAmount)} for ${selectedLiability.liabilityTitle} to Lotus.`,
        meta: {
          targetCollection: 'LoanPayments',
          targetId: paymentRef.id,
          liabilityId: selectedLiability.id,
          lotusTransactionId,
          paymentTransactionGroupId: paymentTag
        }
      });

      setPayments((currentPayments) => [createdPayment, ...currentPayments]);
      setPaymentShow(false);
      setSelectedLiability(null);
      setPaymentForm(buildEmptyPaymentForm());
      swal(
        'Payment submitted',
        `Submitted ${formatKes(paymentAmount)} to Lotus and saved the liability payment as pending while the transfer processes.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to record liability payment:', error);
      swal('Error', error.message || 'The payment could not be submitted to Lotus.', 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const reconcilePendingPayments = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || payments.length === 0) {
        return;
      }

      const pendingPayments = payments.filter((payment) => {
        const status = resolveLiabilityPaymentStatus(payment);
        return Boolean(payment.lotusTransactionId) && ['pending', 'processing', 'submitted'].includes(normalizeStatus(status));
      });

      if (pendingPayments.length === 0) {
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const reconciledAt = Timestamp.now();

        await Promise.all(pendingPayments.map(async (payment) => {
          const response = await fetch(`${LOTUS_API_BASE_URL}/payments/${payment.lotusTransactionId}`, {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          });

          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            return;
          }

          const lotusPaymentData = payload?.data?.transaction || payload?.data || payload || {};
          const lotusPaymentDetails = extractLotusPaymentDetails(payload);
          const lotusStatusRaw = lotusPaymentDetails.lotusTransactionStatus;
          const normalizedStatus = String(lotusStatusRaw || '').trim().toLowerCase();
          const paymentStatus = ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'].includes(normalizedStatus)
            ? 'Paid'
            : ['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error'].includes(normalizedStatus)
              ? 'Failed'
              : null;

          if (!paymentStatus || normalizeStatus(payment.paymentStatus || payment.status) === paymentStatus.toLowerCase()) {
            return;
          }

          const paymentRef = doc(db, 'LoanPayments', payment.id);
          await setDoc(paymentRef, {
            paymentStatus,
            status: paymentStatus,
            lotusTransactionStatus: lotusStatusRaw || paymentStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          if (paymentStatus === 'Paid') {
            const liabilityRecord = liabilities.find((item) => item.id === payment.liabilityId);
            const nextPayments = payments.map((item) => (
              item.id === payment.id
                ? {
                    ...item,
                    paymentStatus,
                    status: paymentStatus,
                    lotusTransactionStatus: lotusStatusRaw || paymentStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            ));
            const successfulPayments = nextPayments.filter((item) => (
              item.liabilityId === payment.liabilityId && isSuccessfulPayment(item)
            ));
            const totalPaidAmount = successfulPayments.reduce((sum, item) => sum + normalizeAmount(item.amount), 0);
            const principalAmount = normalizeAmount(liabilityRecord?.principalAmount || payment.liabilityPrincipalAmount || payment.amount);
            const remainingAmount = Math.max(principalAmount - totalPaidAmount, 0);

            await setDoc(doc(db, 'Loans', payment.liabilityId), {
              remainingAmount,
              totalPaidAmount,
              paymentCount: nextPayments.filter((item) => item.liabilityId === payment.liabilityId).length,
              paymentStatus: remainingAmount <= 0 ? 'Settled' : 'Partially paid',
              status: remainingAmount <= 0 ? 'Settled' : (liabilityRecord?.liabilityStatus || 'Active'),
              lastPaymentAt: payment.paymentDate || payment.createdAt || reconciledAt,
              updatedAt: serverTimestamp()
            }, { merge: true });

            if (!cancelled) {
              setPayments(nextPayments);
            }
          } else if (!cancelled) {
            setPayments((currentPayments) => currentPayments.map((item) => (
              item.id === payment.id
                ? {
                    ...item,
                    paymentStatus,
                    status: paymentStatus,
                    lotusTransactionStatus: lotusStatusRaw || paymentStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            )));
          }
        }));
      } catch (error) {
        console.error('Failed to reconcile Lotus liability payments:', error);
      }
    };

    reconcilePendingPayments();
    const intervalId = window.setInterval(reconcilePendingPayments, LOTUS_RECONCILE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [auth, liabilities, payments]);

  const handleDeleteLiability = async (liability) => {
    if (!liability) {
      return;
    }

    const confirmed = await swal({
      title: 'Delete liability?',
      text: 'This removes the record and its visible summary from the page.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true
    });

    if (!confirmed) {
      return;
    }

    setDeletingLiability(true);

    try {
      await deleteDoc(doc(db, 'Loans', liability.id));

      logAdminActivity({
        action: 'Deleted liability record',
        details: `Deleted ${liability.liabilityTitle} for ${liability.counterpartyName}.`,
        meta: {
          targetCollection: 'Loans',
          targetId: liability.id
        }
      });

      swal('Deleted', 'Liability removed successfully.', 'success');
      await loadPage();
      if (selectedLiability?.id === liability.id) {
        setSelectedLiability(null);
      }
    } catch (error) {
      console.error('Failed to delete liability:', error);
      swal('Error', 'The liability could not be deleted right now.', 'error');
    } finally {
      setDeletingLiability(false);
    }
  };

  const activeSelectedLiability = selectedLiability
    ? liabilityRows.find((item) => item.id === selectedLiability.id) || selectedLiability
    : null;
  const liabilityDetailRows = activeSelectedLiability ? activeSelectedLiability.paymentHistory : [];

  return (
    <div className="liabilityPage debtsLoansPage">
      <header className="debtsLoansHeader">
        <div>
          <span className="debtsLoansEyebrow">Finance / Liabilities</span>
          <h1>Debts &amp; loans</h1>
          <p>Track outstanding balances, due dates, and repayments.</p>
        </div>
        <button type="button" className="debtsLoansPrimaryButton" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} /> Add liability
        </button>
      </header>

      <section className="debtsLoansOverview" aria-label="Liabilities summary">
        <div className="debtsLoansMetric">
          <span>Total outstanding</span>
          <strong>{formatKes(summary.outstanding)}</strong>
        </div>
        <div className="debtsLoansMetric">
          <span>Open obligations</span>
          <strong>{summary.openCount}</strong>
        </div>
        <div className="debtsLoansMetric">
          <span>Due soon or overdue</span>
          <strong>{summary.dueSoonCount}</strong>
        </div>
        <div className="debtsLoansMetric">
          <span>Paid this month</span>
          <strong>{formatKes(summary.paidThisMonth)}</strong>
        </div>
      </section>

      <section className="debtsLoansRegister">
        <div className="debtsLoansRegisterHeader">
          <div>
            <h2>Liability register</h2>
            <p>View the balance and repayment status for each obligation.</p>
          </div>
          <div className="debtsLoansFilters">
            <input
              id="liabilities-search"
              type="search"
              placeholder="Search liabilities"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search liabilities"
            />
            <select
              id="liabilities-type-filter"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              aria-label="Filter by category"
            >
              <option value="All">All categories</option>
              {LIABILITY_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <select
              id="liabilities-status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              aria-label="Filter by status"
            >
              <option value="All">All statuses</option>
              {LIABILITY_STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <select
              id="liabilities-branch-filter"
              value={branchFilter}
              onChange={(event) => setBranchFilter(event.target.value)}
              aria-label="Filter by branch"
            >
              <option value="All">All branches</option>
              <option value="General">General</option>
              {branchesList.map((branch) => <option key={branch.id} value={branch.id}>{branch.branchName}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="debtsLoansState"><Spinner animation="border" role="status" /><span>Loading liabilities...</span></div>
        ) : filteredRows.length === 0 ? (
          <div className="debtsLoansEmpty">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <h3>{liabilityRows.length === 0 ? 'No liabilities recorded' : 'No matching liabilities'}</h3>
            <p>{liabilityRows.length === 0 ? 'Add a debt or loan to begin tracking its repayments.' : 'Try changing your search or filters.'}</p>
            {liabilityRows.length === 0 ? (
              <button type="button" className="debtsLoansPrimaryButton" onClick={openCreateModal}>
                <FontAwesomeIcon icon={faPlus} /> Add liability
              </button>
            ) : null}
          </div>
        ) : (
          <div className="debtsLoansTableWrap">
            <table className="debtsLoansTable">
              <thead>
                <tr>
                  <th>Liability</th>
                  <th>Branch</th>
                  <th>Principal</th>
                  <th>Paid</th>
                  <th>Remaining</th>
                  <th>Due date</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((liability) => {
                  const tone = liability.paymentTone;
                  return (
                    <tr key={liability.id}>
                      <td data-label="Liability">
                        <div className="debtsLoansName">
                          <strong>{liability.liabilityTitle}</strong>
                          <span><FontAwesomeIcon icon={getCategoryIcon(liability.liabilityType)} /> {liability.liabilityType} · {liability.counterpartyName}</span>
                        </div>
                      </td>
                      <td data-label="Branch">{liability.branchName}</td>
                      <td data-label="Principal">{formatKes(liability.principalAmount)}</td>
                      <td data-label="Paid">{formatKes(liability.totalPaidAmount)}</td>
                      <td data-label="Remaining" className="debtsLoansRemaining">{formatKes(liability.remainingAmount)}</td>
                      <td data-label="Due date">{formatShortDate(liability.dueDate, '—')}</td>
                      <td data-label="Status">
                        <div className="debtsLoansStatusCell">
                          <span className={`debtsLoansStatus debtsLoansStatus--${tone}`}>{getToneLabel(tone)}</span>
                          <small>{liability.liabilityStatus} · {liability.paymentCount} payment{liability.paymentCount === 1 ? '' : 's'}</small>
                        </div>
                      </td>
                      <td data-label="Actions" className="debtsLoansActionsCell">
                        <button type="button" className="debtsLoansTextButton" onClick={() => openDetailsModal(liability)}>View</button>
                        <button type="button" className="debtsLoansTextButton debtsLoansTextButton--primary" onClick={() => openPaymentModal(liability)}>Pay</button>
                        <button type="button" className="debtsLoansTextButton" onClick={() => openEditModal(liability)}>Edit</button>
                        <button
                          type="button"
                          className="debtsLoansTextButton debtsLoansTextButton--danger"
                          onClick={() => handleDeleteLiability(liability)}
                          disabled={deletingLiability}
                        >Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <LiabilityFormModal
        show={formShow}
        onHide={() => {
          if (!savingLiability) {
            setFormShow(false);
            clearForm();
          }
        }}
        formMode={formMode}
        formState={formState}
        branchesList={branchesList}
        bankOptions={bankOptions}
        loadingBanks={loadingBanks}
        bankLoadError={bankLoadError}
        handleFormChange={handleFormChange}
        onSubmit={submitLiability}
        isSubmitting={savingLiability}
      />

      <LiabilityPaymentModal
        show={paymentShow}
        onHide={() => {
          if (!savingPayment) {
            setPaymentShow(false);
            setSelectedLiability(null);
            setPaymentForm(buildEmptyPaymentForm());
          }
        }}
        liability={activeSelectedLiability}
        paymentForm={paymentForm}
        handlePaymentFormChange={handlePaymentFormChange}
        onSubmit={submitPayment}
        isSubmitting={savingPayment}
      />

      <LiabilityDetailsModal
        show={detailsShow}
        onHide={() => {
          setDetailsShow(false);
          setSelectedLiability(null);
        }}
        liability={activeSelectedLiability}
        paymentHistory={liabilityDetailRows}
        onRecordPayment={() => {
          if (selectedLiability) {
            setDetailsShow(false);
            setPaymentShow(true);
          }
        }}
        onEdit={() => {
          if (selectedLiability) {
            setDetailsShow(false);
            openEditModal(selectedLiability);
          }
        }}
        onDelete={() => {
          if (selectedLiability) {
            setDetailsShow(false);
            handleDeleteLiability(selectedLiability);
          }
        }}
      />
    </div>
  );
}

function LiabilityFormModal({
  show,
  onHide,
  formMode,
  formState,
  branchesList,
  bankOptions,
  loadingBanks,
  bankLoadError,
  handleFormChange,
  onSubmit,
  isSubmitting
}) {
  return (
    <Modal show={show} onHide={isSubmitting ? undefined : onHide} centered size="lg" contentClassName="debtsLoansModal">
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{formMode === 'edit' ? 'Edit liability' : 'Add liability'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="liabilityModalIntro">
          Track bank loans, supplier balances, legal claims, court orders, fines, asset finance, and any other obligation we owe.
        </div>

        <div className="liabilityModalGrid">
          <Form.Group>
            <Form.Label>Branch</Form.Label>
            <Form.Select name="selectedCampus" value={formState.selectedCampus} onChange={handleFormChange}>
              <option value="General">General</option>
              {branchesList.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Category</Form.Label>
            <Form.Select name="liabilityType" value={formState.liabilityType} onChange={handleFormChange}>
              {LIABILITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Counterparty / lender</Form.Label>
            <Form.Control
              name="counterpartyName"
              value={formState.counterpartyName}
              onChange={handleFormChange}
              placeholder="Bank, vendor, person, court, agency..."
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control
              name="liabilityTitle"
              value={formState.liabilityTitle}
              onChange={handleFormChange}
              placeholder="Bank loan, court order, supplier debt..."
            />
          </Form.Group>

          <Form.Group className="liabilityModalGridFull">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="liabilityDescription"
              value={formState.liabilityDescription}
              onChange={handleFormChange}
              placeholder="Add context, terms, or why this obligation exists."
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Principal amount</Form.Label>
            <Form.Control
              type="number"
              min="1"
              name="principalAmount"
              value={formState.principalAmount}
              onChange={handleFormChange}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select name="liabilityStatus" value={formState.liabilityStatus} onChange={handleFormChange}>
              {LIABILITY_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Start date</Form.Label>
            <Form.Control
              type="datetime-local"
              name="startDate"
              value={formState.startDate}
              onChange={handleFormChange}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Due date</Form.Label>
            <Form.Control
              type="datetime-local"
              name="dueDate"
              value={formState.dueDate}
              onChange={handleFormChange}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Reference number</Form.Label>
            <Form.Control
              name="referenceNumber"
              value={formState.referenceNumber}
              onChange={handleFormChange}
              placeholder="Loan ref, court ref, invoice ref..."
            />
          </Form.Group>

          <div className="liabilityModalGridFull liabilityModalNote">
            <div className="liabilityModalIntro">
              Repayment setup
            </div>
            <div className="liabilityModalNoteText">
              Save the method we will use to settle this obligation so future payments stay ready and consistent.
            </div>
          </div>

          <Form.Group>
            <Form.Label>Repayment method</Form.Label>
            <Form.Select
              name="repaymentMethod"
              value={formState.repaymentMethod}
              onChange={handleFormChange}
            >
              {REPAYMENT_METHOD_OPTIONS.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {formState.repaymentMethod === 'Bank Transfer' && (
            <>
              <Form.Group>
                <Form.Label>Bank name</Form.Label>
                <Form.Select
                  name="repaymentBankId"
                  value={formState.repaymentBankId}
                  onChange={handleFormChange}
                  disabled={loadingBanks}
                >
                  <option value="">{loadingBanks ? 'Loading banks...' : 'Select bank from Lotus'}</option>
                  {bankOptions.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.name}{bank.code ? ` • ${bank.code}` : ''}
                    </option>
                  ))}
                </Form.Select>
                {bankLoadError && <small className="text-danger d-block mt-2">{bankLoadError}</small>}
                <small className="text-muted d-block mt-2">
                  The bank list is pulled from Lotus so repayment data stays consistent.
                </small>
              </Form.Group>

              <Form.Group>
                <Form.Label>Bank account number</Form.Label>
                <Form.Control
                  name="repaymentBankAccountNumber"
                  value={formState.repaymentBankAccountNumber}
                  onChange={handleFormChange}
                  placeholder="Account number"
                />
              </Form.Group>
            </>
          )}

          {formState.repaymentMethod === 'MPESA' && (
            <Form.Group>
              <Form.Label>MPESA phone number</Form.Label>
              <Form.Control
                name="repaymentMpesaNumber"
                value={formState.repaymentMpesaNumber}
                onChange={handleFormChange}
                placeholder="07..."
              />
            </Form.Group>
          )}

          {formState.repaymentMethod === 'Paybill' && (
            <>
              <Form.Group>
                <Form.Label>Paybill number</Form.Label>
                <Form.Control
                  name="repaymentPaybillNumber"
                  value={formState.repaymentPaybillNumber}
                  onChange={handleFormChange}
                  placeholder="Paybill number"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Paybill account number</Form.Label>
                <Form.Control
                  name="repaymentPaybillAccountNumber"
                  value={formState.repaymentPaybillAccountNumber}
                  onChange={handleFormChange}
                  placeholder="Account number"
                />
              </Form.Group>
            </>
          )}

          {formState.repaymentMethod === 'Till Number' && (
            <Form.Group>
              <Form.Label>Till number</Form.Label>
              <Form.Control
                name="repaymentTillNumber"
                value={formState.repaymentTillNumber}
                onChange={handleFormChange}
                placeholder="Till number"
              />
            </Form.Group>
          )}

          {formState.repaymentMethod !== 'Bank Transfer' && formState.repaymentMethod !== 'MPESA' && formState.repaymentMethod !== 'Paybill' && formState.repaymentMethod !== 'Till Number' ? (
            <Form.Group>
              <Form.Label>Repayment reference</Form.Label>
              <Form.Control
                name="repaymentReference"
                value={formState.repaymentReference}
                onChange={handleFormChange}
                placeholder="Cheque number, cash note, or settlement hint"
              />
            </Form.Group>
          ) : (
            <Form.Group className="liabilityModalGridFull">
              <Form.Label>Repayment reference / note</Form.Label>
              <Form.Control
                name="repaymentReference"
                value={formState.repaymentReference}
                onChange={handleFormChange}
                placeholder="Cheque number, settlement note, or payout hint"
              />
            </Form.Group>
          )}

          <Form.Group className="liabilityModalGridFull">
            <Form.Label>Internal notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formState.notes}
              onChange={handleFormChange}
              placeholder="Add settlement notes, legal notes, or payment context."
            />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span> Saving...</span>
            </>
          ) : formMode === 'edit' ? 'Save changes' : 'Save liability'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function LiabilityPaymentModal({
  show,
  onHide,
  liability,
  paymentForm,
  handlePaymentFormChange,
  onSubmit,
  isSubmitting
}) {
  return (
    <Modal show={show} onHide={isSubmitting ? undefined : onHide} centered size="lg" contentClassName="debtsLoansModal">
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Record payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {liability ? (
          <div className="liabilityPaymentModalBody">
            <div className="liabilityPaymentPreview">
              <span>{liability.liabilityType}</span>
              <h6>{liability.liabilityTitle}</h6>
              <p>{liability.counterpartyName}</p>
              <strong>{formatKes(liability.remainingAmount)} remaining</strong>
              <p>
                Repayment: {getRepaymentSummary(liability).method} • {getRepaymentSummary(liability).detail}
              </p>
              <p>Submitting this payment to Lotus will mark it pending until the transfer is verified.</p>
            </div>

            <div className="liabilityModalGrid">
              <Form.Group>
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max={liability.remainingAmount || undefined}
                  name="amount"
                  value={paymentForm.amount}
                  onChange={handlePaymentFormChange}
                  placeholder="Amount paid"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Payment date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="paymentDate"
                  value={paymentForm.paymentDate}
                  onChange={handlePaymentFormChange}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Payment method</Form.Label>
                <Form.Select
                  name="paymentMethod"
                  value={paymentForm.paymentMethod}
                  onChange={handlePaymentFormChange}
                >
                  {PAYMENT_METHOD_OPTIONS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Reference</Form.Label>
                <Form.Control
                  name="reference"
                  value={paymentForm.reference}
                  onChange={handlePaymentFormChange}
                  placeholder="Receipt, transfer, or bank ref"
                />
              </Form.Group>

              <Form.Group className="liabilityModalGridFull">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={paymentForm.notes}
                  onChange={handlePaymentFormChange}
                  placeholder="Optional payment notes"
                />
              </Form.Group>
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span> Saving...</span>
            </>
          ) : 'Save payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function LiabilityDetailsModal({
  show,
  onHide,
  liability,
  paymentHistory,
  onRecordPayment,
  onEdit,
  onDelete
}) {
  return (
    <Modal show={show} onHide={onHide} centered size="xl" contentClassName="debtsLoansModal">
      <Modal.Header closeButton>
        <Modal.Title>Liability details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {liability ? (
          <div className="liabilityDetailsBody">
            <div className="liabilityDetailsHero">
              <div>
                <span>{liability.liabilityType}</span>
                <h5>{liability.liabilityTitle}</h5>
                <p>{liability.counterpartyName}</p>
              </div>
              <div className="liabilityDetailsHeroStats">
                <div>
                  <span>Principal</span>
                  <strong>{formatKes(liability.principalAmount)}</strong>
                </div>
                <div>
                  <span>Paid</span>
                  <strong>{formatKes(liability.totalPaidAmount)}</strong>
                </div>
                <div>
                  <span>Remaining</span>
                  <strong>{formatKes(liability.remainingAmount)}</strong>
                </div>
              </div>
            </div>

            <div className="liabilityDetailsGrid">
              <div>
                <span>Branch</span>
                <strong>{liability.branchName}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{liability.liabilityStatus}</strong>
              </div>
              <div>
                <span>Payment status</span>
                <strong>{liability.paymentStatus}</strong>
              </div>
              <div>
                <span>Due date</span>
                <strong>{formatShortDate(liability.dueDate, 'No due date')}</strong>
              </div>
              <div>
                <span>Start date</span>
                <strong>{formatShortDate(liability.startDate, 'No start date')}</strong>
              </div>
              <div>
                <span>Reference</span>
                <strong>{liability.referenceNumber || '—'}</strong>
              </div>
              <div>
                <span>Repayment method</span>
                <strong>{getRepaymentSummary(liability).method}</strong>
              </div>
              <div>
                <span>Repayment details</span>
                <strong>{getRepaymentSummary(liability).detail}</strong>
              </div>
            </div>

            {liability.liabilityDescription ? (
              <div className="liabilityDetailsNote">
                <span>Description</span>
                <p>{liability.liabilityDescription}</p>
              </div>
            ) : null}

            <div className="liabilityDetailsActions">
              <button type="button" className="liabilityGhostBtn" onClick={onRecordPayment}>
                <FontAwesomeIcon icon={faMoneyBillWave} /> Record payment
              </button>
              <button type="button" className="liabilityGhostBtn" onClick={onEdit}>
                <FontAwesomeIcon icon={faPenToSquare} /> Edit
              </button>
              <button type="button" className="liabilityGhostBtn liabilityGhostBtn--danger" onClick={onDelete}>
                <FontAwesomeIcon icon={faTrashCan} /> Delete
              </button>
            </div>

            <div className="liabilityHistoryBlock">
                <div className="liabilityHistoryHeader">
                  <div>
                    <h6>Payment history</h6>
                    <p>Pending submissions stay visible here, and only successful payments are counted against the balance.</p>
                  </div>
                <span>{paymentHistory.length} record{paymentHistory.length === 1 ? '' : 's'}</span>
              </div>

              {paymentHistory.length > 0 ? (
                <div className="liabilityLedgerTableWrap">
                  <table className="liabilityLedgerTable">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Reference</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatShortDate(payment.paymentDate || payment.createdAt)}</td>
                          <td>{formatKes(payment.amount)}</td>
                          <td>{payment.paymentMethod || '—'}</td>
                          <td>{resolveLiabilityPaymentStatus(payment) || 'Pending'}</td>
                          <td>{payment.reference || '—'}</td>
                          <td>{payment.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="liabilitiesEmptyState liabilitiesEmptyState--compact">
                  <FontAwesomeIcon icon={faReceipt} />
                  <p>No payment records yet for this liability.</p>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={onRecordPayment}>
          Record payment
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DebtsComp;
